import { AspectRatio, ContextType } from "@/services/bedrock/dtos/DesignPackage";
import type { DesignBasis } from "./NewDesignPage";
import { STYLE_CHIPS, STYLE_GROUPS, StyleChip } from "./styleChips";

export interface BriefInput {
  basis: DesignBasis;
  contextType: ContextType;
  format: "single" | "carousel";
  aspectRatio: AspectRatio | "auto";
  chips: StyleChip[];
  notes: string;
}

const SIZE_WORDS: Record<AspectRatio, string> = {
  square_1_1: "square",
  portrait_4_5: "4:5 portrait",
  portrait_9_16: "9:16 story",
};

const KIND_WORDS: Record<ContextType, string> = {
  review: "client review card",
  player: "player spotlight card",
  none: "branded card",
};

// The generator relies on this sentence, so every brief carries it even with no chips.
const INVARIANT = "Use the branch theme colors and keep the player's name, headshot, and compliance line dynamic.";

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export function structureSentence(input: Pick<BriefInput, "contextType" | "format" | "aspectRatio">): string {
  const size = input.aspectRatio === "auto" ? null : SIZE_WORDS[input.aspectRatio];
  const kind = KIND_WORDS[input.contextType];

  let body: string;
  if (input.format === "carousel") {
    // Carousels default to 3 slides in the brief; the generator picks 2 to 5.
    const carousel = input.contextType === "review" ? "3-slide carousel of client reviews" : `3-slide ${kind} carousel`;
    body = size ? `${size} ${carousel}` : carousel;
  } else {
    body = size ? `${size}, single-image ${kind}` : `single-image ${kind}`;
  }

  const sentence = `A ${body}.`;
  return size ? sentence : `${sentence} Let the design choose the best size.`;
}

function chipSentences(chips: StyleChip[]): string[] {
  const selected = new Set(chips.map(chip => chip.id));
  return STYLE_GROUPS.flatMap(group => {
    const fragments = STYLE_CHIPS.filter(chip => chip.group === group.id && selected.has(chip.id)).map(
      chip => chip.fragment
    );
    return fragments.length > 0 ? [`${capitalize(fragments.join("; "))}.`] : [];
  });
}

function notesSentence(notes: string): string | null {
  const trimmed = notes.trim();
  if (!trimmed) return null;
  return capitalize(/[.!?]$/.test(trimmed) ? trimmed : `${trimmed}.`);
}

/** The exact text the generator receives: structure, chips by group, notes, then the invariant. */
export function composeBrief(input: BriefInput): string {
  const notes = notesSentence(input.notes);
  return [structureSentence(input), ...chipSentences(input.chips), ...(notes ? [notes] : []), INVARIANT].join(" ");
}
